import { useEffect, useRef, useState } from "react";

/** Animates a number from its previous value to `target` over `duration`ms (ease-out cubic).
 * Used for KPI headline numbers — the "count up on load" feel of a modern analytics dashboard,
 * without any extra dependency. Jumps straight to the target for prefers-reduced-motion. */
export function useCountUp(target: number, duration = 700): number {
  const [display, setDisplay] = useState(target);
  const prevTarget = useRef(0);
  const raf = useRef<number>();
  const first = useRef(true);

  useEffect(() => {
    const reduce =
      typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce || !Number.isFinite(target)) {
      setDisplay(target);
      prevTarget.current = target;
      return;
    }
    // On first mount, animate up from 0; afterwards, animate from whatever was last shown.
    const from = first.current ? 0 : prevTarget.current;
    first.current = false;
    const to = target;
    if (from === to) {
      setDisplay(to);
      return;
    }
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(from + (to - from) * eased);
      if (t < 1) {
        raf.current = requestAnimationFrame(tick);
      } else {
        prevTarget.current = to;
      }
    };
    raf.current = requestAnimationFrame(tick);
    return () => {
      if (raf.current) cancelAnimationFrame(raf.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, duration]);

  return display;
}
